import React, { useCallback, useRef, useState, useEffect } from "react";
import { Typography } from "@material-ui/core";
import { Tree } from "antd";
import debounce from "lodash/debounce";
import { useTranslation } from "@/langs/useTranslation";
import modalConfirm from "@/components/Modal/modalConfirm";
import { flattenTreeData } from "@/helpers/utils";
import {
  addNode,
  deleteNode,
  modifyDataNode,
  moveNode,
  onSearchTree,
  treeAddParentId,
  uuid
} from "./utils";
import CategoryNode from "./CategoryNode";
import styles from "./CategoryTree.module.less";

const CategoryTree = React.memo(
  ({
    setTreeData,
    treeData = [],
    searchValue,
    checkStrictly,
    checkable,
    selectable,
    editable,
    sortable,
    importable,
    readOnly,
    showAll,
    selectedKeys: initSelectedKeys,
    expandedKeys: initExpandedKeys,
    checkedKeys: initCheckedKeys,
    onCheck,
    onSelect,
    onNodeMove,
    onNodeRename,
    onInputRename,
    onNodeImport,
    onNodeAdd,
    onNodeDelete
  }) => {
    const [draggable, setDraggable] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [showTreeData, setShowTreeData] = useState([]);
    const [checkedKeys, setCheckedKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const flattenCompanyTreeRef = useRef([]);

    const debounceSetExpandedKeys = useRef(debounce(setExpandedKeys, 300));
    const expandedKeysRef = useRef([]);
    const { t } = useTranslation();

    // console.log("-------checkedKeys----",checkedKeys)

    const onDragEnter = info => {
      debounceSetExpandedKeys.current(info.expandedKeys);
      expandedKeysRef.current = info.expandedKeys;
    };

    // const getParentIds = (selfId, flattenData) => {
    //   const parentIds = [];
    //   function recursive(id) {
    //     for (const item of flattenData) {
    //       if (item.id === id) {
    //         parentIds.push(item.parentId);
    //         recursive(item.parentId);
    //         break;
    //       }
    //     }
    //   }
    //   recursive(selfId);
    //   return parentIds;
    // };

    const recursiveGetChildren = startNode => {
      const results = [];
      function recursive(node) {
        if (node.children) {
          node.children.forEach(item => {
            results.push(item);
            recursive(item);
          });
        }
      }

      recursive(startNode);

      return results;
    };

    const handleCheck = (checkedKeys, event) => {
      // setCheckedKeys(checkedKeys);
      // onCheck?.(checkedKeys, event);
      // // console.log("-----6666-----",checkedKeys)
      // return

      if (checkStrictly) {
        const nodeChildren = recursiveGetChildren(event.node);
        const nodeChildrenkeys = nodeChildren.map(node => node.key);
        if (event.checked) {
          // const parentKeys = getParentIds(
          //   event.node.key,
          //   flattenCompanyTreeRef.current
          // );
          const checkedKeys = Array.from(
            new Set([
              ...event.checkedNodes.map(node => node.key),
              ...nodeChildrenkeys
              // ...parentKeys
            ])
          );
          setCheckedKeys(checkedKeys);
          onCheck?.(checkedKeys, {
            ...event,
            checkedNodes: [...event.checkedNodes, ...nodeChildren]
          });
        } else {
          setCheckedKeys(prevChecks => {
            const newKeys = prevChecks.filter(
              key => key !== event.node.key && !nodeChildrenkeys.includes(key)
            );
            onCheck?.(newKeys, event);
            return newKeys;
          });
        }
      } else {
        const checkedKeys = event.checkedNodes.map(node => node.key);
        // console.log('---vvvv--',checkedKeys)
        // console.log('--bbbb--',event)
        setCheckedKeys(checkedKeys);
        onCheck?.(checkedKeys, event);
      }
      // halfCheckedKeys.current = event.halfCheckedKeys || [];
    };

    const handleSelect = (selectedKeys, info) => {
      setSelectedKeys(selectedKeys);
      onSelect?.(selectedKeys, info);
    };

    const onExpand = keys => {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
    };

    const handleSearchTree = value => {
      if (value) {
        setDraggable(false);
        const tree = treeAddParentId(treeData);
        const searchInfo = onSearchTree(tree, value);
        if (showAll) {
          setShowTreeData(treeData.slice(0, 1).concat(searchInfo.searchTree));
        } else {
          setShowTreeData(searchInfo.searchTree);
        }
        setExpandedKeys(searchInfo.expandedKeys);
        if (searchInfo.firstSearchNode) {
          setSelectedKeys([searchInfo.firstSearchNode.key]);
        }
      } else {
        setDraggable(true);
        setShowTreeData([]);
        setExpandedKeys(expandedKeysRef.current);
      }
    };

    const onDrop = info => {
      const dropKey = info.node.key;
      const dragKey = info.dragNode.key;
      const dropPos = info.node.pos.split("-");
      const dropPosition =
        info.dropPosition - Number(dropPos[dropPos.length - 1]);

      const loop = (data, key, callback) => {
        for (let i = 0; i < data.length; i++) {
          if (data[i].key === key) {
            return callback(data[i], i, data);
          }

          if (data[i].children) {
            loop(data[i].children, key, callback);
          }
        }
      };

      const data = [...treeData];

      let dragObj;
      loop(data, dragKey, (item, index, arr) => {
        arr.splice(index, 1);
        dragObj = item;
      });

      if (!info.dropToGap) {
        loop(data, dropKey, item => {
          item.children = item.children || [];
          item.children.unshift(dragObj);
        });
      } else if (
        (info.node.props.children || []).length > 0 &&
        info.node.props.expanded &&
        dropPosition === 1
      ) {
        loop(data, dropKey, item => {
          item.children = item.children || [];
          item.children.unshift(dragObj);
        });
      } else {
        let ar = [];
        let i;
        loop(data, dropKey, (_item, index, arr) => {
          ar = arr;
          i = index;
        });

        if (dropPosition === -1) {
          ar.splice(i, 0, dragObj);
        } else {
          ar.splice(i + 1, 0, dragObj);
        }
      }

      setExpandedKeys(keys => [...new Set(keys.concat([dropKey]))]);
      let dropList = data;
      if (!info.dropToGap) {
        dropList = modifyDataNode(dropList, dragKey, { parentId: dropKey });
      }
      setTreeData(dropList);
    };

    const onAddNode = useCallback(
      async (nodeInfo, value) => {
        const res = await onNodeAdd(nodeInfo, value);
        setTreeData(
          modifyDataNode(treeData, nodeInfo.key, {
            key: res.id,
            id: res.id,
            orderNO: res.order_no,
            count: 0,
            title: value,
            checkable: undefined,
            edit: false
          })
        );
      },
      [setTreeData, treeData]
    );

    const onMoveNode = useCallback(
      async (id, isDown) => {
        const result = moveNode(treeData, id, isDown);
        await onNodeMove(result, isDown);
        setTreeData(result.tree);
      },
      [treeData]
    );

    const handleNodeAdd = useCallback(
      nodeInfo => {
        const nodeKey = uuid();
        const node = {
          title: t({ defaultMessage: "未命名" }),
          key: nodeKey,
          id: nodeKey,
          parentId: nodeInfo.id,
          checkable: false,
          orderNO: nodeInfo.children?.length ?? 0,
          edit: true
        };

        if (nodeInfo.id) {
          setExpandedKeys(value => value.concat([nodeInfo.id]));
        }

        setTreeData(addNode(treeData, nodeInfo.id, node, true));
        setDraggable(false);
      },
      [setTreeData, treeData]
    );

    const onChangeRename = useCallback(
      async (info, value) => {
        await onNodeRename(info, value);
        setTreeData(modifyDataNode(treeData, info.key, { title: value }));
      },
      [setTreeData, treeData]
    );

    const onChangeDelete = useCallback(
      (node, fromServer) => {
        modalConfirm({
          title: t({ defaultMessage: "提示信息" }),
          content: t({ defaultMessage: "确定要删除吗？" }),
          onConfirm: async () => {
            if (fromServer) {
              await onNodeDelete(node);
            }
            setTreeData(deleteNode(treeData, node.key));
          }
        });
      },
      [setTreeData, treeData]
    );

    const titleRender = info => {
      return (
        <CategoryNode
          nodeInfo={info}
          onChangeRename={onChangeRename}
          onInputRename={onInputRename}
          onAddNode={onAddNode}
          onChangeDelete={onChangeDelete}
          onNodeAdd={handleNodeAdd}
          onMoveNode={onMoveNode}
          onNodeImport={onNodeImport}
          setDraggable={setDraggable}
          searchValue={searchValue}
          readOnly={readOnly}
          editable={editable}
          sortable={sortable}
          importable={importable}
        />
      );
    };

    useEffect(() => {
      if (searchValue) {
        handleSearchTree(searchValue);
      }
    }, [searchValue]); // eslint-disable-line

    useEffect(() => {
      if (initExpandedKeys) {
        setExpandedKeys(initExpandedKeys);
      }
    }, [initExpandedKeys]);

    useEffect(() => {
      if (initCheckedKeys) {
        console.log('-----initCheckedKeys------',initCheckedKeys)
        setCheckedKeys(initCheckedKeys);
      }
    }, [initCheckedKeys]);

    useEffect(() => {
      if (initSelectedKeys) {
        setSelectedKeys(initSelectedKeys);
      }
    }, [initSelectedKeys]);

    useEffect(() => {
      if (checkStrictly) {
        flattenCompanyTreeRef.current = flattenTreeData(
          /* searchValue ? showTreeData : */ treeData
        );
      }
    }, [/* searchValue, showTreeData, */ treeData, checkStrictly]);
    return (
      <div className={`company-tree-box`}>
        {(searchValue ? showTreeData.length : treeData.length) ? (
          <Tree
            className={styles.draggableTree}
            // draggable={draggable}
            blockNode
            checkStrictly={true}
            checkable={checkable}
            selectable={selectable}
            onDragEnter={onDragEnter}
            onDrop={onDrop}
            onExpand={onExpand}
            onCheck={handleCheck}
            onSelect={handleSelect}
            // selectedKeys={selectedKeys}
            expandedKeys={expandedKeys}
            checkedKeys={checkedKeys}
            titleRender={titleRender}
            treeData={searchValue ? showTreeData : treeData}
          />
        ) : (
          <div className={styles.empty}>
            <Typography variant="body2" color="textSecondary">
              {searchValue
                ? t({ defaultMessage: "无相关结果" })
                : t({ defaultMessage: "暂无分类" })}
            </Typography>
          </div>
        )}
      </div>
    );
  }
);

export default CategoryTree;
